import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import styles from './language-selector.module.css';
import { CSLocale } from '../../typescript/component';
import { setLocalStorageItem } from '../../sdk/utils';
import { usePageState } from '../../context/pageContext';
import { ResourceTypeMap } from '../../helper/utils';

interface LanguageSelectorProps {
  languages: CSLocale[] | null;
  currentLocale: string;
  onLocaleChange: (newLocale: string) => void;
  className?: string;
  linkClassName?: string;
  hideButton?: boolean;
  hideDropdown?: boolean;
  header_strings?: Record<string, string>;
}

export default function LanguageSelector({
  languages,
  currentLocale,
  onLocaleChange,
  className,
  linkClassName,
  hideButton=false,
  hideDropdown=false,
  header_strings
}: LanguageSelectorProps) {
  // Available locales: If an empty array, all locales are available
  const [availableLocales] = usePageState('availableLocales');
  const [isVisible, setIsVisible] = usePageState('isLanguageDropdownOpen');
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();

  const toggleVisibility = () => setIsVisible(prev => !prev);

  // Only resource pages report their locales. For every other page an empty list
  // means "unknown", so ask the server whether the translated URL exists before
  // navigating there instead of landing the visitor on a 404.
  const isPublishedIn = async (newLocale: string) => {
    if (availableLocales.length > 0) return availableLocales.includes(newLocale);

    const path = router.asPath.split('#')[0];
    const localizedPath = newLocale === router.defaultLocale ? path : `/${newLocale}${path === '/' ? '' : path}`;

    try {
      const res = await fetch(localizedPath, { method: 'HEAD' });
      return res.status !== 404;
    } catch {
      return true;
    }
  };

  const changeLocale = async (newLocale: string) => {
    onLocaleChange(newLocale);
    setIsVisible(false);
    setLocalStorageItem('preferred_locale', newLocale);

    if (await isPublishedIn(newLocale)) {
      router.push(router.pathname, router.asPath, { locale: newLocale }); // ✅ replace locale cleanly
      return;
    }

    // The current entry isn't published in the selected locale, so send the
    // visitor to the closest page that does exist there: the resources landing
    // page filtered to this resource type, or the home page as a last resort.
    const segments = router.asPath.split('?')[0].split('/').filter(Boolean);
    const resourceType =
      segments[0] === 'resources' && segments[1]
        ? ResourceTypeMap[segments[1] as keyof typeof ResourceTypeMap]
        : undefined;

    router.push(
      resourceType ? `/resources?type=${resourceType}` : '/',
      undefined,
      { locale: newLocale }
    );
  };

  useEffect(() => {
    setIsMobile(typeof window !== 'undefined' && window.innerWidth <= 979);

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 979);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!languages || languages.length === 0) return null;

  return (
    <div className={`${styles.languageLink}${className ? ` ${className}` : ''}${hideButton ? ` ${styles.languageLinkNoButton}` : ''}`}>
      {hideButton === false && (
      <button className={styles.languageDropdown} onClick={toggleVisibility}>
        <span className={linkClassName}>{header_strings?.languages_button ? header_strings?.languages_button : 'Languages'}</span>
      </button>
      )}

      {isVisible && hideDropdown === false && (
        <div className={`${styles.languageSelector} ${styles.visible}`}>
          <div className={styles.title}>
            <div className={styles.selectYourLanguage}>{header_strings?.select_your_language_label ? header_strings?.select_your_language_label : 'Select your language'}</div>
            <button
              onClick={toggleVisibility}
              aria-label="Close Icon"
              className={styles.closeIcon}
            >
              <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="Close">
                  <path id="Line 10" d="M2.63574 3.36743L15.3637 16.0954" stroke="#37353C" stroke-linecap="round"/>
                  <path id="Line 11" d="M15.3643 3.36743L2.63633 16.0954" stroke="#37353C" stroke-linecap="round"/>
                </g>
              </svg>
            </button>
          </div>
          <div className={styles.navItemGroup}>
            {languages
              .filter(lang => lang.code !== currentLocale)
              .map(lang => (
                <div
                  key={lang.code}
                  className={styles.navItem}
                  onClick={() => changeLocale(lang.code)}
                >
                  <div className={styles.navLink}>{lang.name}</div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
